import { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router";
import { isRecordPaneRoute } from "@/shared/ui/dock-pages";
import {
  useWorkspace,
  type ComposerSubmit,
  type ComposerSubmitOutcome,
} from "@/shared/ui/WorkspaceContext";
import { useTranslation } from "@/shared/i18n/LocaleProvider";
import {
  useChatSurface,
  type ChatSurfaceApi,
} from "@/features/intelligence/hooks/use-chat-surface";
import type {
  FrontendToolHost,
  HandlerOutcome,
} from "@/features/intelligence/lib/frontend-tool-run";
import type { FrontendToolCapabilities } from "@/features/intelligence/lib/frontend-tools";
import { planStartAdvisory, reportStartAdvisory } from "@/features/intelligence/lib/start-advisory";
import { prepareAdvisory } from "@/features/intelligence/lib/prepare-advisory";
import { prepareMeetingBrief } from "@/features/intelligence/lib/meeting-brief";
import { prepareOrderInstructions } from "@/features/intelligence/lib/order-instructions";
import { preparePortfolioProposal } from "@/features/intelligence/lib/portfolio-proposal";
import { prepareProposalMail } from "@/features/intelligence/lib/proposal-mail";
import { prepareRiskExplanation } from "@/features/intelligence/lib/risk-explanation";
import { prepareDecisionChain } from "@/features/intelligence/lib/decision-chain";
import { prepareCashFlow } from "@/features/intelligence/lib/cash-flow";
import { prepareAnnualFollowup } from "@/features/intelligence/lib/annual-followup";
import { showAppIntro } from "@/features/intelligence/lib/app-intro";
import { useAccess } from "@/shared/api/grants";
import { chatToolCardAnswered } from "@/features/intelligence/home/chat-tool-cards";
import {
  planOpenCreateClient,
  reportFillNewClientPage,
  reportOpenCreateClient,
} from "@/features/intelligence/lib/open-create-client";
import { fillNewClientPage } from "@/features/advice-portal/lib/new-client-fill";
import { startPath } from "@/features/forms/shared/record/record-route";
import { useViewedRecord } from "@/features/forms/shared/record/viewed-record";
import { recordFrontendContext } from "@/features/forms/frontend-context";
import { useViewedPage } from "@/features/forms/shared/record/viewed-page";
import { resolveClientId } from "@/features/intelligence/lib/client-id-resolve";
import { clientBookWhenLoaded, LIVE_CLIENT_BOOK } from "@/features/intelligence/lib/client-book";
import { useCreateClientToolArmed } from "@/features/intelligence/hooks/use-access";
import { viewFrontendContext } from "@/features/intelligence/lib/view-context";
import { useClientRows } from "@/features/clients/lib/row-store";
import type { TurnOutcome } from "@/features/intelligence/lib/thread-state";
import {
  setHomeConversationId,
  useHomeConversationId,
} from "@/features/intelligence/home/model/conversation";
import { useSimonConversationNavigation } from "@/features/intelligence/home/hooks/open-conversation";
import {
  clientDetailsFillTarget,
  useClientDetailsFillOffered,
} from "@/features/forms/shared/record/client-details-fill";
import {
  clientDetailsFillOf,
  NO_CLIENT_DETAILS,
  reportClientDetailsFill,
} from "@/features/intelligence/lib/fill-client-details";

export interface HomeChat {
  surface: ChatSurfaceApi;
  conversationId: string | null;
  submit: (text: string) => void;
}

export function useHomeChat(): HomeChat {
  const { locale } = useTranslation();
  const {
    pendingComposerSubmit,
    consumeComposerSubmit,
    openClientProfile,
    openCreateClientPrefilled,
    chatContext,
    showIntro,
  } = useWorkspace();
  const superuser = useAccess()?.superuser === true;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const conversationId = useHomeConversationId();
  const recordPaneClosed = !isRecordPaneRoute(pathname);
  const viewedRecord = useViewedRecord();
  const viewedPage = useViewedPage();
  const clientRows = useClientRows().rows;
  const frontendContext = useMemo(
    () =>
      recordFrontendContext(viewedRecord, viewedPage) ??
      viewFrontendContext(chatContext, pathname, clientRows),
    [viewedRecord, viewedPage, chatContext, pathname, clientRows],
  );

  const openCreateClientRef = useRef(openCreateClientPrefilled);
  openCreateClientRef.current = openCreateClientPrefilled;
  const addClientArmed = useCreateClientToolArmed();
  const fillOffered = useClientDetailsFillOffered();
  const { armClose, finishClose } = useCloseAfterTurn();

  const frontendTools = useMemo(
    (): { capabilities: FrontendToolCapabilities; host: FrontendToolHost } => ({
      capabilities: {
        advisoryStart: recordPaneClosed,
        createClientPanel: addClientArmed && recordPaneClosed,
        clientDetailsFill: fillOffered,
        meetingBrief: true,
        orderInstructions: true,
        portfolioProposal: true,
        proposalMail: true,
        riskExplanation: true,
        decisionChain: true,
        cashFlow: true,
        annualFollowup: true,
        appIntro: superuser ? "superuser" : "advisor",
      },
      host: {
        showClient: async ({ clientId }): Promise<HandlerOutcome> => {
          const resolved = await resolveClientId(clientId, await clientBookWhenLoaded());
          if (!resolved.ok) {
            return { ok: false, message: resolved.message };
          }

          openClientProfile(resolved.row.id);
          return { ok: true, message: resolved.message };
        },

        closeConversation: armClose,

        startAdvisory: async ({ clientId }): Promise<HandlerOutcome> => {
          const plan = await planStartAdvisory(clientId, await clientBookWhenLoaded());
          if (!plan.press) {
            return { ok: false, message: plan.message };
          }
          navigate(startPath(plan.subject));
          return reportStartAdvisory(plan);
        },

        prepareAdvisory,

        prepareMeetingBrief,

        prepareOrderInstructions,

        preparePortfolioProposal,

        prepareProposalMail,

        prepareRiskExplanation,

        prepareDecisionChain,

        prepareCashFlow,

        prepareAnnualFollowup,

        showAppIntro: ({ focus }) => showAppIntro(focus, superuser, showIntro),

        reported: chatToolCardAnswered,

        openCreateClient: async (args): Promise<HandlerOutcome> => {
          if (!recordPaneClosed) return PANEL_ROOM_GONE;
          const plan = planOpenCreateClient(args);
          const filled = fillNewClientPage(plan.prefill);
          if (filled !== null) return reportFillNewClientPage(plan, filled);
          const opened = openCreateClientRef.current(plan.prefill);
          return reportOpenCreateClient(plan, opened);
        },

        fillClientDetails: (args): HandlerOutcome => {
          const target = clientDetailsFillTarget();
          if (target === null) return NO_CLIENT_DETAILS;
          return reportClientDetailsFill(target(clientDetailsFillOf(args)));
        },
      },
    }),
    [
      navigate,
      openClientProfile,
      recordPaneClosed,
      addClientArmed,
      fillOffered,
      armClose,
      superuser,
      showIntro,
    ],
  );

  const surface = useChatSurface(
    locale,
    conversationId,
    setHomeConversationId,
    frontendContext,
    frontendTools,
  );

  const { send, attach, busy } = surface;

  const submit = useCallback(
    (value: string) => {
      const text = value.trim();
      if (!text) return;
      LIVE_CLIENT_BOOK.warm();
      void send(text).finally(finishClose);
    },
    [send, finishClose],
  );

  useEffect(() => {
    if (pendingComposerSubmit == null || busy) return;
    const request = pendingComposerSubmit;
    consumeComposerSubmit();
    void handOff(request, attach, send)
      .catch((): ComposerSubmitOutcome => REFUSED)
      .then((outcome) => {
        request.onSettled?.(outcome);
        finishClose();
      });
  }, [pendingComposerSubmit, busy, consumeComposerSubmit, attach, send, finishClose]);

  return { surface, conversationId, submit };
}

function useCloseAfterTurn(): { armClose: () => HandlerOutcome; finishClose: () => void } {
  const { closeConversation } = useSimonConversationNavigation();
  const closeRef = useRef(closeConversation);
  closeRef.current = closeConversation;
  const pending = useRef<number | null>(null);

  const finishClose = useCallback(() => {
    if (pending.current === null) return;
    window.clearTimeout(pending.current);
    pending.current = null;
    closeRef.current();
  }, []);

  const armClose = useCallback((): HandlerOutcome => {
    if (pending.current === null) {
      pending.current = window.setTimeout(finishClose, CLOSE_AFTER_TOOL_MS);
    }
    return CLOSING;
  }, [finishClose]);

  useEffect(
    () => () => {
      if (pending.current !== null) window.clearTimeout(pending.current);
      pending.current = null;
    },
    [],
  );

  return { armClose, finishClose };
}

async function handOff(
  request: ComposerSubmit,
  attach: ChatSurfaceApi["attach"],
  send: ChatSurfaceApi["send"],
): Promise<ComposerSubmitOutcome> {
  const text = request.text.trim();
  if (!text) return REFUSED;
  LIVE_CLIENT_BOOK.warm();
  const files = request.files ?? [];
  let attached = 0;
  for (const file of files) if (await attach(file)) attached += 1;
  if (files.length > 0 && attached === 0) return { sent: false, reason: "no-upload" };
  const turn = await send(text);
  return turn === null ? REFUSED : { sent: true, turn: TURN_ENDS[turn] };
}

export const CLOSE_AFTER_TOOL_MS = 8_000;

const CLOSING: HandlerOutcome = {
  ok: true,
  message:
    "The conversation is closing: the start page will show as soon as this answer " +
    "ends. Say goodbye in one short sentence and nothing more.",
};

const REFUSED: ComposerSubmitOutcome = { sent: false, reason: "refused" };

const TURN_ENDS = {
  complete: "complete",
  truncated: "stopped",
  errored: "failed",
  interrupted: "lost",
  running: "lost",
  idle: "lost",
} as const satisfies Record<TurnOutcome, Extract<ComposerSubmitOutcome, { sent: true }>["turn"]>;

const PANEL_ROOM_GONE: HandlerOutcome = {
  ok: false,
  message:
    "The new-client form was NOT opened: the advisor has an advisory open, which " +
    "fills their screen, and opening a form over it would bury work they are in " +
    "the middle of. Nothing was filled in and NOTHING HAS BEEN CREATED. Ask them " +
    'to close the advisory first, or to use "Ny kund" themselves.',
};
